import posthog, { type BeforeSendFn, type PostHogConfig, type Properties } from "posthog-js";
import { getStorageItem, setStorageItem, removeStorageItem, STORAGE_KEYS } from '@/lib/pwa-storage';
import {
  classifyAnalyticsUrl,
  isSensitiveAuthKey,
  maskVideoDeliveryToken,
  sanitizeAnalyticsUrl,
} from '@/lib/analytics-url-policy';
import {
  ANALYTICS_SUPER_PROPERTIES,
  APP_PLATFORM,
  type AppPlatform,
} from '@/lib/analytics-super-properties';
import { getBrandId } from '@/lib/brands';
import { enableRouteGuardedSessionRecording } from '@/lib/session-recording-route-guard';
import { postHogCampaignProperties, recordLandingVisitOnBoot } from '@/lib/marketing-attribution';

let isInitialized = false;
let activeMode: 'web' | 'native' | null = null;

// Pendiente de identificar: guarda los datos del usuario si PostHog no estaba listo
let pendingIdentification: { userId: string; properties?: Record<string, any> } | null = null;

/**
 * Eventos capturados antes de que PostHog estuviera listo.
 *
 * Existe porque `initPostHog()` NUNCA puede ocurrir en el primer commit: vive en
 * un efecto de `CookieConsentProvider` tras un guard `isLoading`, y `isLoading`
 * solo pasa a `false` dentro de *otro* efecto (`use-cookie-consent`). Cualquier
 * evento emitido desde un efecto de montaje —típicamente una **impresión**— se
 * perdía en silencio, así que las tasas «vistas → clics» salían con el
 * numerador lleno y el denominador a cero.
 *
 * **En memoria, nunca en localStorage** (a diferencia de la identificación
 * pendiente). Es el punto que mantiene esto limpio con el consentimiento: si el
 * visitante rechaza, `initPostHog()` no llega a ejecutarse, la cola no se vacía
 * y muere con la página. Nada de comportamiento pre-consentimiento sobrevive a
 * la pestaña.
 */
let pendingEvents: Array<{ eventName: string; properties?: Record<string, any> }> = [];

/**
 * Techo de la cola. Solo se encolan eventos de la ventana previa a `init` (dos
 * commits), así que un puñado basta; el límite está para que un bucle de
 * captura no crezca sin freno. Al llenarse se descartan los NUEVOS y se
 * conservan los primeros: en esa ventana lo que importa son las impresiones del
 * primer render.
 */
const PENDING_EVENTS_LIMIT = 50;

// Debug mode
const DEBUG_POSTHOG = process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_DEBUG_AUTH === 'true';

// ⚠️ TEMPORAL: Flag para forzar PostHog sin consentimiento (solo para optimización de campaña)
// IMPORTANTE: Esto NO cumple con GDPR. Usar solo temporalmente y desactivar después.
const FORCE_POSTHOG_ENABLED = process.env.NEXT_PUBLIC_POSTHOG_FORCE_ENABLE === 'true';

function logDebug(message: string, data?: unknown) {
  if (DEBUG_POSTHOG && typeof window !== 'undefined') {
    console.log(`[PostHog] ${message}`, data ?? '');
  }
}

export type PostHogInitOptions = {
  mode?: 'web' | 'native';
};

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const POSTHOG_PROJECT_TOKEN_PROPERTY = 'token';
const POSTHOG_URL_PROPERTY_KEYS = new Set([
  '$current_url',
  '$referrer',
  '$initial_referrer',
  '$session_entry_url',
  '$el_href',
  '$link_url',
  'url',
  'href',
]);

function isSensitivePropertyKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return (
    isSensitiveAuthKey(normalized) ||
    normalized.includes('email') ||
    normalized.includes('phone') ||
    normalized.includes('token') ||
    normalized.includes('authorization')
  );
}

function isPlainObject(value: object): boolean {
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function sanitizePostHogProperties(properties: Properties): Properties {
  const sanitized: Properties = { ...properties };

  for (const [key, value] of Object.entries(properties)) {
    // PostHog injects its public project key here and drops captures if it is removed.
    if (key === POSTHOG_PROJECT_TOKEN_PROPERTY) continue;

    if (isSensitiveAuthKey(key)) {
      delete sanitized[key];
      continue;
    }

    if (typeof value === 'string' && POSTHOG_URL_PROPERTY_KEYS.has(key.toLowerCase())) {
      sanitized[key] = sanitizeAnalyticsUrl(value);
    }
  }

  return sanitized;
}

/**
 * Ruido de terceros que `capture_exceptions` recoge por window.onerror y
 * unhandledrejection. Nada de esto sale de nuestro bundle: llega sin stack
 * accionable (o sin fichero y línea siquiera) y gasta cuota de error tracking.
 * Cada patrón queda atado a su origen conocido para poder revisarlo luego.
 */
const THIRD_PARTY_EXCEPTION_NOISE = [
  // Script que el preview de Outlook / Office Safe Links inyecta al abrir un
  // enlace desde el correo.
  'Object Not Found Matching Id',
  // Error cross-origin sin CORS: por diseño no trae fichero, línea ni stack.
  'Script error.',
  // Un Event de fallo de carga de recurso colándose por window.onerror.
  'Event captured as exception with keys: isTrusted',
  // React reconciliando un DOM que mutó por fuera: Google Translate y extensiones.
  "Failed to execute 'removeChild' on 'Node'",
  // Símbolo de una extensión de Safari; su mundo aislado no ve app/polyfills.ts,
  // así que el polyfill defensivo de EmptyRanges nunca llega a aplicarse.
  'EmptyRanges',
]

type PostHogEvent = NonNullable<Parameters<BeforeSendFn>[0]>

function matchesThirdPartyNoise(message: unknown): boolean {
  return (
    typeof message === 'string' &&
    THIRD_PARTY_EXCEPTION_NOISE.some(pattern => message.includes(pattern))
  )
}

function isThirdPartyExceptionNoise(event: PostHogEvent): boolean {
  if (event.event !== '$exception') return false

  const properties: Properties = event.properties ?? {}
  if (matchesThirdPartyNoise(properties.$exception_message)) return true

  const list: unknown = properties.$exception_list
  if (!Array.isArray(list)) return false
  return (list as unknown[]).some(entry =>
    matchesThirdPartyNoise((entry as { value?: unknown } | null)?.value)
  )
}

/**
 * Recorre un valor y transforma cada string, a cualquier profundidad (arrays y
 * objetos planos); `dropKey` descarta claves de objeto por el camino. El resto
 * (`Date`, instancias) se devuelve tal cual. Un solo recorrido para los dos
 * usos de este fichero, para que la idea de «objeto plano» viva en un sitio.
 */
function mapStringsDeep(
  value: unknown,
  transform: (text: string) => string,
  dropKey?: (key: string) => boolean
): unknown {
  if (typeof value === 'string') return transform(value);
  if (Array.isArray(value)) return value.map(item => mapStringsDeep(item, transform, dropKey));
  if (!value || typeof value !== 'object' || !isPlainObject(value)) return value;

  const mapped: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    if (dropKey?.(key)) continue;
    mapped[key] = mapStringsDeep(nested, transform, dropKey);
  }
  return mapped;
}

/**
 * Filtro `before_send`: puro, evento → evento | null. Tras limpiar credenciales
 * de las propiedades de URL, el token de la entrega se enmascara en el evento
 * ENTERO (`properties`, `$set`, `$set_once` y lo que la librería añada): PostHog
 * copia la URL en más sitios de los que controlamos (ADR-055).
 */
const sanitizePostHogEvent: BeforeSendFn = event => {
  if (!event) return null
  if (isThirdPartyExceptionNoise(event)) return null
  const sanitized = { ...event, properties: sanitizePostHogProperties(event.properties) }
  return mapStringsDeep(sanitized, maskVideoDeliveryToken) as PostHogEvent
};

function sanitizeAnalyticsValue(value: unknown): unknown {
  return mapStringsDeep(
    value,
    text => sanitizeAnalyticsUrl(text).replace(EMAIL_PATTERN, '[redacted]'),
    isSensitivePropertyKey
  );
}

function sanitizeAnalyticsProperties(properties?: Record<string, any>): Record<string, any> | undefined {
  if (!properties) return properties;
  return sanitizeAnalyticsValue(properties) as Record<string, any>;
}

function shouldUseNativeMode(options?: PostHogInitOptions): boolean {
  return options?.mode === 'native';
}

function resolveAppPlatform(useNativeMode: boolean): AppPlatform {
  return useNativeMode ? APP_PLATFORM.CAPACITOR : APP_PLATFORM.WEB;
}

function buildPostHogInitConfig(
  useNativeMode: boolean,
  apiHost: string,
  uiHost: string
): Partial<PostHogConfig> {
  return {
    api_host: apiHost,
    ui_host: uiHost,
    // Pin de defaults recomendados de PostHog (fija el comportamiento por fecha).
    defaults: '2026-05-30',
    capture_exceptions: true,
    disable_capture_url_hashes: true,
    debug: false,
    before_send: sanitizePostHogEvent,
    // Session replay arranca aparte, en idle (ver lib/session-recording-route-guard.ts):
    // cargar y evaluar posthog-recorder.js costaba ~152 ms de frame bloqueante
    // justo tras `load`, el mayor contribuyente medido al INP de campo.
    disable_session_recording: true,
    ...(useNativeMode
      ? {
          persistence: 'localStorage',
          autocapture: false,
          // Pageviews restore funnel visibility in Capacitor without session replay.
          capture_pageview: true,
          disable_surveys: true,
        }
      : {}),
  };
}

function registerAppPlatform(useNativeMode: boolean): void {
  const appPlatform = resolveAppPlatform(useNativeMode);
  posthog.register({
    [ANALYTICS_SUPER_PROPERTIES.APP_PLATFORM]: appPlatform,
    [ANALYTICS_SUPER_PROPERTIES.DEPLOYMENT_BRAND]: getBrandId(),
    ...postHogCampaignProperties(),
  });
  logDebug('✅ PostHog initialized successfully', { app_platform: appPlatform });
}

function identifyPropertiesForMode(
  properties: Record<string, any> | undefined
): Record<string, any> | undefined {
  return activeMode === 'native' ? sanitizeAnalyticsProperties(properties) : properties;
}

function flushPendingIdentification(): void {
  const stored = loadPendingIdentification();
  if (stored) {
    pendingIdentification = stored;
  }
  if (!pendingIdentification) return;

  logDebug('🔄 Processing pending user identification', {
    userId: pendingIdentification.userId.substring(0, 8) + '...',
  });
  try {
    posthog.identify(
      pendingIdentification.userId,
      identifyPropertiesForMode(pendingIdentification.properties)
    );
    logDebug('✅ Pending user identified successfully');
    clearPendingIdentification();
    pendingIdentification = null;
  } catch (error) {
    console.error('[PostHog] Failed to identify pending user:', error);
    // Keep pendingIdentification so a later init/retry can finish identify.
  }
}

/** Envía lo encolado antes de `init`, en el orden en que se capturó. */
function flushPendingEvents(): void {
  if (pendingEvents.length === 0) return;

  const queued = pendingEvents;
  // Se vacía ANTES de reenviar. Se llama desde `initPostHog` una vez
  // `isInitialized` ya es `true`, así que `captureEvent` toma el camino de
  // envío; pero dejar la cola llena mientras se itera sería un bucle esperando
  // a que algo la mirase con `isInitialized` en falso.
  pendingEvents = [];

  logDebug('🔄 Flushing events captured before init', { count: queued.length });
  for (const { eventName, properties } of queued) {
    captureEvent(eventName, properties);
  }
}

function handleAlreadyInitialized(requestedMode: 'web' | 'native'): void {
  if (activeMode !== requestedMode) {
    console.warn(
      `[PostHog] Already initialized in ${activeMode} mode; ignoring ${requestedMode} init request`
    );
    return;
  }

  const stored = loadPendingIdentification();
  if (!stored) return;

  logDebug('🔄 Processing stored pending identification after init');
  try {
    posthog.identify(stored.userId, identifyPropertiesForMode(stored.properties));
    clearPendingIdentification();
    logDebug('✅ Stored pending user identified successfully');
  } catch (error) {
    console.error('[PostHog] Failed to identify stored pending user:', error);
  }
}

/**
 * Verifica si PostHog debe inicializarse sin consentimiento
 * ⚠️ TEMPORAL: Solo para optimización de campaña
 */
export function shouldForcePostHog(): boolean {
  return FORCE_POSTHOG_ENABLED;
}

/**
 * Carga la identificación pendiente desde localStorage
 */
function loadPendingIdentification(): { userId: string; properties?: Record<string, any> } | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = getStorageItem(STORAGE_KEYS.POSTHOG_PENDING_IDENTIFICATION);
    if (stored) {
      const parsed = JSON.parse(stored);
      logDebug('📦 Loaded pending identification from storage', {
        userId: parsed.userId?.substring(0, 8) + '...'
      });
      return parsed;
    }
  } catch (error) {
    logDebug('⚠️ Failed to load pending identification from storage', error);
  }
  return null;
}

/**
 * Guarda la identificación pendiente en localStorage
 */
function savePendingIdentification(identification: { userId: string; properties?: Record<string, any> }): void {
  if (typeof window === 'undefined') return;

  try {
    setStorageItem(STORAGE_KEYS.POSTHOG_PENDING_IDENTIFICATION, JSON.stringify(identification));
    logDebug('💾 Saved pending identification to storage');
  } catch (error) {
    logDebug('⚠️ Failed to save pending identification to storage', error);
  }
}

/**
 * Limpia la identificación pendiente de localStorage
 */
function clearPendingIdentification(): void {
  if (typeof window === 'undefined') return;

  try {
    removeStorageItem(STORAGE_KEYS.POSTHOG_PENDING_IDENTIFICATION);
    logDebug('🗑️ Cleared pending identification from storage');
  } catch (error) {
    logDebug('⚠️ Failed to clear pending identification from storage', error);
  }
}

/**
 * Inicializa PostHog solo si el usuario ha dado consentimiento
 * Esta función debe ser llamada explícitamente después de obtener consentimiento
 */
export function initPostHog(options?: PostHogInitOptions): void {
  const useNativeMode = shouldUseNativeMode(options);
  const requestedMode = useNativeMode ? 'native' : 'web';

  if (isInitialized) {
    handleAlreadyInitialized(requestedMode);
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  // OAuth callbacks must exchange/clear their credentials before any analytics SDK starts.
  // La entrega del vídeo (`capability`) NO cuenta aquí: PostHog la mide con el token enmascarado (ADR-055).
  if (classifyAnalyticsUrl() === 'credential') {
    logDebug('Skipping initialization while the current URL contains OAuth credentials');
    return;
  }

  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!apiKey) {
    console.warn('[PostHog] API key not found, skipping initialization');
    return;
  }

  // Host de ingesta: por defecto el reverse proxy gestionado de PostHog (t.papanoel.chat).
  // En local/test se sobreescribe vía NEXT_PUBLIC_POSTHOG_HOST (.env.local).
  const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://t.papanoel.chat";
  const uiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || "https://eu.posthog.com";

  try {
    logDebug('🚀 Initializing PostHog', { apiHost, uiHost });
    posthog.init(apiKey, buildPostHogInitConfig(useNativeMode, apiHost, uiHost));
    registerAppPlatform(useNativeMode);
    isInitialized = true;
    activeMode = requestedMode;
    flushPendingIdentification();
    flushPendingEvents();
    // Session replay: nunca en native app (App Store Guideline 5.1.2(i), ATT).
    if (!useNativeMode) enableRouteGuardedSessionRecording();
  } catch (error) {
    console.error('[PostHog] Failed to initialize:', error);
  }
}

/**
 * Captura eventos de analytics a través de un único boundary.
 *
 * Si PostHog aún no está listo el evento se **encola** en vez de descartarse:
 * ver `pendingEvents` para por qué el primer commit siempre llega antes de
 * `init` y por qué eso falseaba cualquier tasa medida desde una impresión.
 */
export function captureEvent(eventName: string, properties?: Record<string, any>): void {
  if (!isPostHogInitialized()) {
    if (pendingEvents.length < PENDING_EVENTS_LIMIT) {
      pendingEvents.push({ eventName, properties });
      logDebug('⏳ PostHog not ready, queued event', { eventName });
    } else {
      logDebug('⚠️ Pending event queue full, dropping event', { eventName });
    }
    return;
  }

  // En native app se eliminan propiedades sensibles antes de enviarlas.
  const safeProperties =
    activeMode === 'native' ? sanitizeAnalyticsProperties(properties) : properties;

  try {
    posthog.capture(eventName, safeProperties);
    logDebug('📊 Event captured', { eventName, properties: safeProperties });
  } catch (error) {
    console.error('[PostHog] Failed to capture event:', error);
  }
}

/**
 * Verifica si PostHog está inicializado
 */
export function isPostHogInitialized(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return isInitialized;
}

/**
 * Identifica al usuario en PostHog
 * Si PostHog no está inicializado aún, guarda los datos para identificar cuando se inicialice
 * La identificación se persiste en localStorage para sobrevivir recargas de página
 */
export function identifyUser(userId: string, properties?: Record<string, any>): void {
  const safeProperties = activeMode === 'native' ? sanitizeAnalyticsProperties(properties) : properties;

  logDebug('🔍 identifyUser called', {
    userId: userId.substring(0, 8) + '...',
    isInitialized,
    properties: safeProperties
  });

  if (!isPostHogInitialized()) {
    // Guardar para identificar cuando PostHog se inicialice
    logDebug('⏳ PostHog not ready, saving for later identification');
    pendingIdentification = { userId, properties: safeProperties };
    // Persistir en localStorage para que sobreviva recargas de página
    savePendingIdentification(pendingIdentification);
    return;
  }

  try {
    posthog.identify(userId, safeProperties);
    logDebug('✅ User identified successfully');
    // Limpiar identificación pendiente (en memoria y localStorage) siempre después de identificar exitosamente
    // Esto previene que datos obsoletos en localStorage se re-procesen en futuras inicializaciones
    clearPendingIdentification();
    pendingIdentification = null;
  } catch (error) {
    console.error('[PostHog] Failed to identify user:', error);
  }
}

/**
 * Actualiza las propiedades del usuario en PostHog
 */
export function updateUserProperties(properties: Record<string, any>): void {
  if (!isPostHogInitialized()) {
    logDebug('⏳ Cannot update properties - PostHog not initialized');
    return;
  }

  try {
    const safeProperties = activeMode === 'native' ? sanitizeAnalyticsProperties(properties) : properties;
    posthog.setPersonProperties(safeProperties);
    logDebug('✅ User properties updated', safeProperties);
  } catch (error) {
    console.error('[PostHog] Failed to update user properties:', error);
  }
}

/**
 * Resetea la identidad del usuario en PostHog (para logout)
 */
export function resetUser(): void {
  // Limpiar cualquier identificación pendiente (en memoria y almacenada)
  pendingIdentification = null;
  clearPendingIdentification();

  if (!isPostHogInitialized()) {
    return;
  }

  try {
    posthog.reset();
    logDebug('✅ User reset');
  } catch (error) {
    console.error('[PostHog] Failed to reset user:', error);
  }
}

// Next ejecuta este fichero antes que el frontend: la URL aún es la de la
// landing. Sin consentimiento, el toque se queda en memoria (lib/marketing-attribution).
recordLandingVisitOnBoot();
